/**
 * BookingAmendmentModal
 *
 * Admin-only modal for amending a booking.
 * - AX (Admin): can edit all allowed fields
 * - SAX (SubAdmin): restricted to notes and booking_status only
 * - Reason is mandatory
 * - Shows version history
 * - Shows approval-pending state for high-value changes
 * - View and download e-tickets for each version
 * 
 * 
 */
import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { generateETicketPDF, sendETicketToEmail } from '../../../../utils/eTicketGenerator';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://jetscanner360.com/api/v1';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${sessionStorage.getItem('token')}`,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const fmtUSD = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n) || 0);

const ApprovalBadge = ({ status }) => {
  const map = {
    auto_approved: 'bg-green-100 text-green-700',
    pending: 'bg-amber-100 text-amber-700',
    approved: 'bg-blue-100 text-blue-700',
    rejected: 'bg-red-100 text-red-700',
  };
  const labels = {
    auto_approved: 'Auto-Approved',
    pending: 'Pending AX Approval',
    approved: 'Approved',
    rejected: 'Rejected',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${map[status] || 'bg-gray-100 text-gray-600'}`}>
      {labels[status] || status}
    </span>
  );
};

// ─── Version History Panel ────────────────────────────────────────────────────

const VersionHistory = ({ versions, bookingToken, isAX, onApprove, onReject, onViewETicket, onSendEmail }) => {
  const [sendingEmailVersionId, setSendingEmailVersionId] = useState(null);

  const handleSendEmailClick = (versionId) => {
    setSendingEmailVersionId(versionId);
    onSendEmail(versionId).finally(() => {
      setSendingEmailVersionId(null);
    });
  };

  if (!versions || versions.length === 0) {
    return (
      <p className="text-sm text-gray-500 py-4 text-center">No amendments yet.</p>
    );
  }

  return (
    <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
      
      {[...versions].reverse().map((v) => (
        <div key={v.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50 hover:bg-gray-100 transition-colors">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-yellow-100 text-yellow-700 font-bold text-sm">
                v{v.version_number}
              </span>
              <div>
                <p className="text-sm font-semibold text-gray-900">Version {v.version_number}</p>
                <p className="text-xs text-gray-500">{fmtDate(v.created_at)}</p>
              </div>
            </div>
            <ApprovalBadge status={v.approval_status} />
          </div>

          <div className="space-y-2 mb-4 pl-11">
            <p className="text-xs text-gray-700">
              <span className="font-semibold text-gray-900">Modified by:</span> {v.amendedBy?.first_name} {v.amendedBy?.last_name}
              <span className="ml-2 inline-block px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs font-medium">
                {v.amended_by_role}
              </span>
            </p>
            <p className="text-xs text-gray-700">
              <span className="font-semibold text-gray-900">Reason:</span> {v.reason}
            </p>
            {v.approval_status === 'rejected' && v.rejection_reason && (
              <p className="text-xs text-red-700 bg-red-50 p-2 rounded border border-red-200">
                <span className="font-semibold">Rejection reason:</span> {v.rejection_reason}
              </p>
            )}
          </div>

          {/* E-Ticket Actions - Professional Button Group */}
          <div className="flex gap-2 mt-4 pl-11 flex-wrap">
            {/* View E-Ticket Button */}
            <button
              onClick={() => onViewETicket(v.id, 'view')}
              disabled={sendingEmailVersionId === v.id}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 transition-all duration-200 font-medium text-sm group disabled:opacity-50 disabled:cursor-not-allowed"
              title="View e-ticket in print preview"
            >
              <svg className="w-4 h-4 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              <span>View</span>
            </button>

            {/* Download Button */}
            {/* <button
              onClick={() => onViewETicket(v.id, 'download')}
              disabled={sendingEmailVersionId === v.id}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 hover:border-green-300 transition-all duration-200 font-medium text-sm group disabled:opacity-50 disabled:cursor-not-allowed"
              title="Download e-ticket as PDF"
            >
              <svg className="w-4 h-4 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span>Download</span>
            </button> */}

            {/* Send Email Button */}
            <button
              onClick={() => handleSendEmailClick(v.id)}
              disabled={sendingEmailVersionId === v.id}
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg hover:bg-purple-100 hover:border-purple-300 transition-all duration-200 font-medium text-sm group disabled:opacity-75 disabled:cursor-not-allowed"
              title={sendingEmailVersionId === v.id ? 'Sending e-ticket...' : "Send e-ticket to JetSetter's email"}
            >
              <svg 
                className={`w-4 h-4 transition-transform ${sendingEmailVersionId === v.id ? 'animate-spin' : 'group-hover:scale-110'}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>{sendingEmailVersionId === v.id ? 'Sending...' : 'Send Email'}</span>
            </button>
          </div>

          {/* AX approval actions for pending versions */}
          {isAX && v.approval_status === 'pending' && (
            <div className="flex gap-2 mt-4 pl-11 pt-3 border-t border-gray-200">
              <button
                onClick={() => onApprove(v.id)}
                disabled={sendingEmailVersionId === v.id}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 hover:border-emerald-300 transition-all duration-200 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Approve</span>
              </button>
              <button
                onClick={() => onReject(v.id)}
                disabled={sendingEmailVersionId === v.id}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 hover:border-red-300 transition-all duration-200 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>Reject</span>
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ─── Main Modal ───────────────────────────────────────────────────────────────

const BookingAmendmentModal = ({ booking, currentUser, onClose, onAmended }) => {
  const isAX =
    currentUser?.user_type === 'Admin' || Number(currentUser?.is_admin) === 1;

  // Amendment form state
  const [reason, setReason] = useState('');
  const [bookingChanges, setBookingChanges] = useState({});
  const [passengerChanges, setPassengerChanges] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Version history state
  const [versions, setVersions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('amend'); // 'amend' | 'history'

  // Rejection modal state
  const [rejectVersionId, setRejectVersionId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  // E-Ticket state
  const [eTicketLoading, setETicketLoading] = useState(false);
  const [eTicketData, setETicketData] = useState(null);

  // Load version history
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/admin/bookings/${booking.booking_token}/amendment-history`,
        { headers: authHeaders() }
      );
      const data = await res.json();
      
      // Handle permission denied (403)
      if (res.status === 403) {
        toast.error(data.message || 'You do not have permission to view booking amendments');
        setHistoryLoading(false);
        return;
      }
      
      if (data.success) setVersions(data.data.versions || []);
    } catch (err) {
      console.error('Failed to load amendment history:', err);
      toast.error('Failed to load amendment history');
    } finally {
      setHistoryLoading(false);
    }
  }, [booking.booking_token]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Handle booking field change
  const handleBookingChange = (field, value) => {
    setBookingChanges((prev) => ({ ...prev, [field]: value }));
  };

  // Handle passenger field change
  const handlePassengerChange = (passengerId, field, value) => {
    setPassengerChanges((prev) => {
      const existing = prev.find((p) => p.passenger_id === passengerId);
      if (existing) {
        return prev.map((p) =>
          p.passenger_id === passengerId ? { ...p, [field]: value } : p
        );
      }
      return [...prev, { passenger_id: passengerId, [field]: value }];
    });
  };

  // Submit amendment
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      toast.error('Amendment reason is required');
      return;
    }
    if (Object.keys(bookingChanges).length === 0 && passengerChanges.length === 0) {
      toast.error('No changes to submit');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(
        `${API_BASE}/admin/bookings/${booking.booking_token}/amend`,
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            reason: reason.trim(),
            booking_changes: bookingChanges,
            passenger_changes: passengerChanges,
          }),
        }
      );
      const data = await res.json();

      // Handle permission denied (403)
      if (res.status === 403) {
        toast.error(data.message || 'You do not have permission to create booking amendments');
        setSubmitting(false);
        return;
      }

      if (!res.ok) {
        if (data.requires_ax_approval) {
          toast.error(`Fare change of ${fmtUSD(data.fare_difference)} requires AX approval`);
        } else {
          toast.error(data.message || 'Amendment failed');
        }
        return;
      }

      toast.success(data.message || 'Booking amended successfully');
      setReason('');
      setBookingChanges({});
      setPassengerChanges([]);
      await loadHistory();
      setActiveTab('history');
      if (onAmended) onAmended();
    } catch (err) {
      toast.error('Failed to submit amendment');
    } finally {
      setSubmitting(false);
    }
  };

  // Approve amendment
  const handleApprove = async (versionId) => {
    try {
      const res = await fetch(
        `${API_BASE}/admin/bookings/${booking.booking_token}/amend/${versionId}/approve`,
        { method: 'POST', headers: authHeaders(), body: JSON.stringify({ reason: 'Approved by AX' }) }
      );
      const data = await res.json();
      if (data.success) {
        toast.success('Amendment approved');
        await loadHistory();
        if (onAmended) onAmended();
      } else {
        toast.error(data.message || 'Approval failed');
      }
    } catch (err) {
      toast.error('Failed to approve amendment');
    }
  };

  // Reject amendment
  const handleRejectConfirm = async () => {
    if (!rejectReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }
    setRejecting(true);
    try {
      const res = await fetch(
        `${API_BASE}/admin/bookings/${booking.booking_token}/amend/${rejectVersionId}/reject`,
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ reason: rejectReason.trim() }),
        }
      );
      const data = await res.json();
      if (data.success) {
        toast.success('Amendment rejected and booking rolled back');
        setRejectVersionId(null);
        setRejectReason('');
        await loadHistory();
        if (onAmended) onAmended();
      } else {
        toast.error(data.message || 'Rejection failed');
      }
    } catch (err) {
      toast.error('Failed to reject amendment');
    } finally {
      setRejecting(false);
    }
  };

  // Handle e-ticket view/download
  const handleViewETicket = async (versionId, action) => {
    setETicketLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/bookings/${booking.booking_token}/e-ticket-data/${versionId}`,
        { headers: authHeaders() }
      );
      const data = await res.json();

      if (!data.success) {
        toast.error(data.message || 'Failed to load e-ticket data');
        return;
      }

      setETicketData(data.data);

      // Generate and display/download PDF
      if (action === 'view') {
        generateETicketPDF(data.data, `e-ticket-${booking.reference_number}-v${data.data.version_number}.pdf`);
        toast.success('E-ticket opened in print preview');
      } else if (action === 'download') {
        generateETicketPDF(data.data, `e-ticket-${booking.reference_number}-v${data.data.version_number}.pdf`);
        toast.success('E-ticket ready to download');
      }
    } catch (err) {
      console.error('Error loading e-ticket:', err);
      toast.error('Failed to load e-ticket');
    } finally {
      setETicketLoading(false);
    }
  };

  // Handle send e-ticket to email
  const handleSendETicketEmail = async (versionId) => {
    try {
      const res = await fetch(
        `${API_BASE}/bookings/${booking.booking_token}/send-e-ticket/${versionId}`,
        {
          method: 'POST',
          headers: authHeaders()
        }
      );
      const data = await res.json();

      if (!data.success) {
        toast.error(data.message || 'Failed to send e-ticket');
        return;
      }

      toast.success(`E-ticket sent to ${data.data.recipient_email}`);
    } catch (err) {
      console.error('Error sending e-ticket:', err);
      toast.error('Failed to send e-ticket to email');
    }
  };

  const passengers = booking.passengers || [];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Amend Booking</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Ref: {booking.reference_number}
                {booking.flight_number ? ` · Flight ${booking.flight_number}` : ''}
              </p>
              <p className="text-xs text-gray-400 mt-0.5 font-mono truncate" title={booking.booking_token}>
                Leg token: {booking.booking_token}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                Role: <strong>{isAX ? 'AX (Super Admin)' : 'SAX (Scoped Admin)'}</strong>
                {' · '}
                Changes apply to this flight leg only
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors text-xl font-bold"
            >
              ✕
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 px-6">
            {['amend', 'history'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-3 px-4 text-sm font-semibold border-b-2 transition-colors capitalize ${activeTab === tab
                    ? 'border-yellow-400 text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                {tab === 'history' ? `Version History (${versions.length})` : 'Amend'}
              </button>
            ))}
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5">

            {/* ── Amend Tab ── */}
            {activeTab === 'amend' && (
              <form onSubmit={handleSubmit} className="space-y-5">

                {/* Reason — mandatory */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
                    Reason for Amendment <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={2}
                    placeholder="Describe why this booking is being amended…"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent resize-none"
                    required
                  />
                </div>

                {/* Booking fields */}
                <div>
                  <p className="text-xs font-bold text-gray-700 mb-3 uppercase tracking-wide">
                    Booking Fields {!isAX && <span className="text-amber-600 font-normal normal-case">(SAX: notes & status only)</span>}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

                    {/* Notes — available to both AX and SAX */}
                    <div className="sm:col-span-2">
                      <label className="block text-xs text-gray-600 mb-1">Admin Notes</label>
                      <textarea
                        rows={2}
                        defaultValue={booking.notes || ''}
                        onChange={(e) => handleBookingChange('notes', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 resize-none"
                        placeholder="Internal notes…"
                      />
                    </div>

                    {/* Booking Status — available to both */}
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Booking Status</label>
                      {(() => {
                        const currentStatus = String(booking.booking_status || '').toLowerCase();
                        const isCancelled = currentStatus === 'cancelled' || currentStatus === 'cancelled (refunded)';
                        return (
                          <>
                            <select
                              defaultValue={booking.booking_status || ''}
                              onChange={(e) => handleBookingChange('booking_status', e.target.value)}
                              disabled={isCancelled}
                              className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 ${
                                isCancelled
                                  ? 'border-red-300 bg-red-50 text-gray-500 cursor-not-allowed'
                                  : 'border-gray-300'
                              }`}
                            >
                              <option value="">— No change —</option>
                              <option value="Confirmed">Confirmed</option>
                              <option value="Completed">Completed</option>
                              <option value="Cancelled">Cancelled</option>
                              <option value="PendingPJOConfirmation">PendingPJOConfirmation</option>
                            </select>
                            {isCancelled && (
                              <p className="text-xs text-red-600 mt-1.5 font-medium">
                                ⚠️ Cannot change status of a {booking.booking_status} booking
                              </p>
                            )}
                          </>
                        );
                      })()}
                    </div>

                    {/* AX-only fields */}
                    {isAX && (
                      <>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Departure Date/Time</label>
                          <input
                            type="datetime-local"
                            defaultValue={booking.departure_date_time ? booking.departure_date_time.slice(0, 16) : ''}
                            onChange={(e) => handleBookingChange('departure_date_time', e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Arrival Date/Time</label>
                          <input
                            type="datetime-local"
                            defaultValue={booking.arrival_date_time ? booking.arrival_date_time.slice(0, 16) : ''}
                            onChange={(e) => handleBookingChange('arrival_date_time', e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Fare Amount (USD)
                            <span className="ml-1 text-amber-600 text-xs">{'>'} $5,000 diff requires AX approval</span>
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            defaultValue={booking.paid_total_amount || ''}
                            onChange={(e) => handleBookingChange('paid_total_amount', e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Passenger fields — AX only (SAX: notes & status per backend) */}
                {isAX && passengers.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-gray-700 mb-3 uppercase tracking-wide">Passengers</p>
                    <div className="space-y-4">
                      {passengers.map((p) => (
                        <div key={p.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                          <p className="text-sm font-semibold text-gray-800 mb-3">
                            {p.first_name} {p.last_name}
                          </p>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">First Name</label>
                              <input
                                type="text"
                                defaultValue={p.first_name}
                                onChange={(e) => handlePassengerChange(p.id, 'first_name', e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Last Name</label>
                              <input
                                type="text"
                                defaultValue={p.last_name}
                                onChange={(e) => handlePassengerChange(p.id, 'last_name', e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Date of Birth</label>
                              <input
                                type="date"
                                defaultValue={p.date_of_birth ? String(p.date_of_birth).slice(0, 10) : ''}
                                onChange={(e) => handlePassengerChange(p.id, 'date_of_birth', e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Email</label>
                              <input
                                type="email"
                                defaultValue={p.email || ''}
                                onChange={(e) => handlePassengerChange(p.id, 'email', e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Mobile</label>
                              <input
                                type="text"
                                defaultValue={p.mobile_number || ''}
                                onChange={(e) => handlePassengerChange(p.id, 'mobile_number', e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Submit */}
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-5 py-2 text-sm font-semibold border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-5 py-2 text-sm font-bold bg-yellow-400 text-gray-900 rounded-lg hover:bg-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {submitting ? 'Saving…' : 'Save Amendment'}
                  </button>
                </div>
              </form>
            )}

            {/* ── History Tab ── */}
            {activeTab === 'history' && (
              historyLoading ? (
                <div className="flex justify-center py-10">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-200 border-t-yellow-400" />
                </div>
              ) : (
                <VersionHistory
                  versions={versions}
                  bookingToken={booking.booking_token}
                  isAX={isAX}
                  onApprove={handleApprove}
                  onReject={(vId) => setRejectVersionId(vId)}
                  onViewETicket={handleViewETicket}
                  onSendEmail={handleSendETicketEmail}
                />
              )
            )}
          </div>
        </div>
      </div>

      {/* Rejection reason modal */}
      {rejectVersionId && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-base font-bold text-gray-900 mb-4">Reject Amendment</h3>
            <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
              Rejection Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="Explain why this amendment is being rejected…"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 resize-none mb-4"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setRejectVersionId(null); setRejectReason(''); }}
                className="px-4 py-2 text-sm font-semibold border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectConfirm}
                disabled={rejecting}
                className="px-4 py-2 text-sm font-bold bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {rejecting ? 'Rejecting…' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BookingAmendmentModal;
